/* eslint-disable react/prop-types */
// @ts-nocheck
// ReactMuiTableListView built on @tanstack/react-table v8. Accepts v7-shape
// column defs via an internal adapter so consumers keep their existing column
// definitions and Cell/Header renderers. Supports sorting, filtering,
// pagination (client + controlled), row selection, column visibility,
// expansion with renderRowSubComponent, and virtualization via
// @tanstack/react-virtual.

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import Divider from '@mui/material/Divider';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { merge, cloneDeep } from 'lodash';
import {
  Box,
  Checkbox,
  Typography,
  Pagination,
  IconButton,
  useTheme,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { useContextMenu } from 'react-contexify';
import { useVirtualizer } from '@tanstack/react-virtual';

import { ReactComponent as SortButtonArrowIcon } from '../../assets/images/SortButtonArrowIcon.svg';
import { ContextMenus } from './ContextMenus';
import NoDataPresentComponent from './NoDataPresentComponent';

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;
    useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate;
      }
    }, [resolvedRef, indeterminate]);
    return <Checkbox sx={{ padding: 0 }} ref={resolvedRef} {...rest} />;
  }
);
IndeterminateCheckbox.propTypes = {
  indeterminate: PropTypes.bool.isRequired,
};

const ReactMuiTablePaginationActions = (props) => {
  const { count, page, rowsPerPage, onPageChange } = props;
  const uniqueId = useId();

  const handlePageChange = useCallback(
    (event, _page) => {
      onPageChange(event, _page - 1);
    },
    [onPageChange]
  );
  return (
    <Box sx={{ flexShrink: 0, marginLeft: (theme) => theme.spacing(2.5) }}>
      <Pagination
        aria-label={`Pagination navigation for table ${uniqueId}`}
        count={
          count <= rowsPerPage && count / rowsPerPage === 0
            ? count / rowsPerPage
            : Math.max(0, Math.ceil(count / rowsPerPage))
        }
        page={page + 1}
        onChange={handlePageChange}
        color="primary"
        size="small"
        boundaryCount={3}
      />
    </Box>
  );
};
ReactMuiTablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const ROW_LEVEL_HOVER_OPTIONS_DEFAULT_STYLE = {
  position: 'absolute',
  float: 'right',
  right: 0,
  height: 'inherit',
  display: 'none',
};

const defaultPropGetter = () => ({});

const DEFAULT_ROW_SELECTION_CONFIG = {
  enable: false,
  mode: 'checkbox',
  showDeleteOnSelected: false,
  type: 'multi-select',
  hideSelectAll: false,
};
const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];
const EMPTY_FN = () => {};

const sortedButton = (v7Col) => {
  return !v7Col.disableSortBy ? (
    <Box
      component="button"
      className="MuiTableSortLabel-root"
      aria-label={`Sort by ${v7Col?.heading}`}
    >
      <SortButtonArrowIcon
        height={12}
        width={12}
        style={{
          color: 'inherit',
          transform: 'translate(0, 2px)',
        }}
      />
    </Box>
  ) : null;
};

// Translate react-table v7 column definitions to TanStack Table v8 ColumnDefs.
// The original v7 def is stashed in `meta.v7` so header/cell renderers can
// keep receiving it in the shape consumers already expect.
const adaptV7Columns = (v7Cols) =>
  (v7Cols || []).map((c) => {
    const id =
      c.id ??
      (typeof c.accessor === 'string' ? c.accessor : undefined) ??
      c.heading ??
      Math.random().toString(36).slice(2);

    const def = {
      id,
      size: c.width,
      enableSorting: !c.disableSortBy,
      enableResizing: true,
      meta: { v7: c },
    };
    if (typeof c.accessor === 'string') {
      def.accessorKey = c.accessor;
    } else if (typeof c.accessor === 'function') {
      def.accessorFn = c.accessor;
    }

    const H = c.Header;
    def.header = () => {
      if (H === undefined || H === null) return c.heading ?? '';
      if (typeof H === 'string') return H;
      if (typeof H === 'function') return <H column={c} />;
      return H;
    };

    const C = c.Cell;
    if (C) {
      def.cell = (ctx) => {
        const r = ctx.row;
        const v7Row = {
          id: r.id,
          original: r.original,
          index: r.index,
          isSelected: r.getIsSelected(),
          toggleRowSelected: (v) => r.toggleSelected(v),
          isExpanded: r.getIsExpanded(),
          toggleRowExpanded: (v) => r.toggleExpanded(v),
          getToggleRowExpandedProps: () => ({
            onClick: r.getToggleExpandedHandler(),
            style: { cursor: 'pointer' },
            title: 'Toggle row expanded',
          }),
        };
        if (typeof C === 'function') {
          return <C row={v7Row} value={ctx.getValue()} column={c} cell={ctx} />;
        }
        return C;
      };
    }

    return def;
  });

const ReactMuiTableListViewV8 = ({
  columns,
  data,
  isDeletebyId = false,
  getHeaderProps = defaultPropGetter,
  getColumnProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
  rowLevelOnHoverOptions = EMPTY_FN,
  externalCustomTheme = EMPTY_OBJECT,
  rowSelectionConfig = DEFAULT_ROW_SELECTION_CONFIG,
  onTableStateChange = defaultPropGetter,
  enableRowSelection = false,
  toggle = false,
  selectedRowIds = undefined,
  enablePagination = false,
  isAllOptionDisabled = true,
  rowsPerPageOptions,
  pageCount: controlledPageCount,
  initialPageSize = 10,
  renderRowSubComponent,
  initialGlobalFilter,
  autoResetExpanded = true,
  noSearchResultMessage = 'No data found.',
  skipPageReset,
  rowLevelRightClickMenus = EMPTY_ARRAY,
  enableControlledPagination = false,
  fetchData = EMPTY_FN,
  dataLength,
  labelDisplayedRows = null,
  showPaginationAction = true,
  enableVirtualization = false,
  maxHeightForVirtualization = document.documentElement.clientHeight * 0.65,
  itemHeightForVirtualization = 40,
  hiddenColumns = EMPTY_ARRAY,
  stickyHeader = false,
  stickyHeaderConfig = EMPTY_OBJECT,
  disableRowSelectionCondition = false,
  maxTableHeight = 450,
}) => {
  const theme = useTheme();
  const { hideAll } = useContextMenu();
  const menuId = useId();

  const newTheme = useMemo(
    () => createTheme(merge(cloneDeep(theme), externalCustomTheme)),
    [theme, externalCustomTheme]
  );

  // v8 ColumnDefs derived from v7-shape input.
  const v8Columns = useMemo(() => {
    const adapted = adaptV7Columns(columns);
    if (rowSelectionConfig?.enable && rowSelectionConfig?.mode === 'checkbox') {
      adapted.unshift({
        id: 'selection',
        size: rowSelectionConfig?.width || 35,
        enableSorting: false,
        enableResizing: false,
        header: ({ table }) => {
          const isMultiSelect = rowSelectionConfig.type === 'multi-select';

          if (isMultiSelect && rowSelectionConfig?.hideSelectAll) {
            return null;
          }

          return (
            <Box
              display="flex"
              alignItems="center"
              textAlign="center"
              ml="-6px"
            >
              {isMultiSelect ? (
                <>
                  <IndeterminateCheckbox
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                    inputProps={{ 'aria-label': 'Toggle all rows selected' }}
                  />
                  {rowSelectionConfig?.showLabel && (
                    <Typography variant="body3" ml={1} color="inherit">
                      SELECT ALL
                    </Typography>
                  )}
                </>
              ) : (
                <Typography
                  variant="body3"
                  sx={{ fontWeight: 700 }}
                  ml={1}
                  color="inherit"
                >
                  SELECT
                </Typography>
              )}
            </Box>
          );
        },
        cell: ({ row }) => {
          const isDisabled =
            typeof disableRowSelectionCondition === 'function'
              ? disableRowSelectionCondition({
                  id: row.id,
                  original: row.original,
                  index: row.index,
                })
              : false;
          const handleChange = () => {
            if (
              rowSelectionConfig.type === 'single-select' &&
              !row.getIsSelected()
            ) {
              row.toggleSelected();
              return;
            }
            row.toggleSelected();
          };
          return (
            <div>
              <IndeterminateCheckbox
                checked={isDisabled ? false : row.getIsSelected()}
                indeterminate={row.getIsSomeSelected()}
                onChange={handleChange}
                disabled={isDisabled}
                inputProps={{ 'aria-label': `Select row ${row.id}` }}
              />
            </div>
          );
        },
      });
    }
    return adapted;
  }, [columns, rowSelectionConfig, disableRowSelectionCondition]);

  const [sorting, setSorting] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter ?? '');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [expanded, setExpanded] = useState({});
  const hiddenColumnsKey = (hiddenColumns || []).join('|');
  const [columnVisibility, setColumnVisibility] = useState(
    Object.fromEntries((hiddenColumns || []).map((id) => [id, false]))
  );

  useEffect(() => {
    const ids = hiddenColumnsKey ? hiddenColumnsKey.split('|') : [];
    setColumnVisibility(Object.fromEntries(ids.map((id) => [id, false])));
  }, [hiddenColumnsKey]);

  useEffect(() => {
    setGlobalFilter(initialGlobalFilter ?? '');
  }, [initialGlobalFilter]);

  useEffect(() => {
    if (selectedRowIds !== undefined) {
      setRowSelection(selectedRowIds || {});
    }
  }, [selectedRowIds]);

  // Sanitize controlledPageCount before passing to TanStack v8.
  // V7 silently ignored NaN/invalid values; v8 does not, which can corrupt
  // pagination state. Use -1 (TanStack convention for "unknown") as fallback.
  const safePageCount = Number.isFinite(controlledPageCount)
    ? controlledPageCount
    : -1;

  const table = useReactTable({
    data: data ?? [],
    columns: v8Columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
      expanded,
      columnVisibility,
      ...(enableControlledPagination
        ? { pagination }
        : enablePagination
          ? { pagination }
          : {}),
    },
    // Sanitize pageCount: V7 silently ignored NaN/invalid values; TanStack v8
    // does not, which can corrupt pagination state. Use -1 (unknown) as fallback.
    pageCount: enableControlledPagination ? safePageCount : undefined,
    manualPagination: enableControlledPagination,
    autoResetPageIndex: !skipPageReset,
    autoResetExpanded,
    getRowId: isDeletebyId ? (row) => row.id : undefined,
    enableRowSelection: !!rowSelectionConfig?.enable,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnResizing: true,
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(enablePagination && !enableControlledPagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(renderRowSubComponent ? { getRowCanExpand: () => true } : {}),
  });

  const gotoPage = useCallback((idx) => table.setPageIndex(idx), [table]);
  const setPageSize = useCallback((size) => table.setPageSize(size), [table]);

  // Row count shown in pagination — matches v7 wrapper: use rows.length unless
  // controlled pagination supplies dataLength.
  const allRows = table.getRowModel().rows;
  const prePaginatedRows = table.getFilteredRowModel().rows;

  // Match v7 behavior: fetchData runs on pagination change when controlled.
  // Guard values so consumers always receive valid numbers (V7 guaranteed this).
  useEffect(() => {
    if (enableControlledPagination && fetchData) {
      const safePageIndex = Number.isFinite(pagination.pageIndex)
        ? pagination.pageIndex
        : 0;
      const safePageSize = Number.isFinite(pagination.pageSize)
        ? pagination.pageSize
        : initialPageSize;
      fetchData(safePageIndex, safePageSize);
    }
  }, [enableControlledPagination, pagination.pageIndex, pagination.pageSize]);

  // Mirror v7 onTableStateChange contract as closely as possible.
  // Guard pagination values to match V7's always-numeric guarantee.
  useEffect(() => {
    if (typeof onTableStateChange === 'function') {
      const safePageIndex = Number.isFinite(pagination.pageIndex)
        ? pagination.pageIndex
        : 0;
      const safePageSize = Number.isFinite(pagination.pageSize)
        ? pagination.pageSize
        : initialPageSize;
      onTableStateChange({
        pageIndex: safePageIndex,
        pageSize: safePageSize,
        selectedRowIds: rowSelection,
        getToggleAllRowsSelectedHandler: table.getToggleAllRowsSelectedHandler,
        toggleAllRowsSelected: (value) =>
          table.toggleAllRowsSelected(value ?? false),
        globalFilter,
        data,
        gotoPage,
      });
    }
  }, [
    rowSelection,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    data,
  ]);

  // v7 `toggle` prop — clears selection when flipped.
  useEffect(() => {
    table.toggleAllRowsSelected(false);
  }, [toggle]);

  const allOptionValueSetter = (optsParam, lenParam) => {
    if (optsParam && [...optsParam, 5, 10, 20, 50].includes(Number(lenParam))) {
      return Number(lenParam) + 1;
    }
    if ([5, 10, 20, 50].includes(Number(lenParam))) {
      return Number(lenParam) + 1;
    }
    return lenParam;
  };

  const handleOnPageChange = useCallback(
    (_event, newPage) => gotoPage(newPage),
    [gotoPage]
  );
  const handleOnRowPerPageChange = useCallback(
    (event) => {
      setPageSize(Number(event?.target?.value));
      gotoPage(0);
    },
    [gotoPage, setPageSize]
  );

  const displayRows = enablePagination
    ? table.getPaginationRowModel().rows
    : allRows;

  // v7-shape helpers for callbacks — most consumers only read .id/.original on
  // row and .value/.column.id/.row on cell. Keep those accessible so existing
  // consumer callbacks work without change.
  const shimRow = (r) => ({
    id: r.id,
    original: r.original,
    index: r.index,
    isSelected: r.getIsSelected(),
    toggleRowSelected: (v) => r.toggleSelected(v),
    isExpanded: r.getIsExpanded(),
    toggleRowExpanded: (v) => r.toggleExpanded(v),
    getToggleRowExpandedProps: () => ({
      onClick: r.getToggleExpandedHandler(),
      style: { cursor: 'pointer' },
      title: 'Toggle row expanded',
    }),
    cells: r.getVisibleCells().map((c) => ({
      value: c.getValue(),
      column: {
        ...(c.column.columnDef.meta?.v7 ?? {}),
        id: c.column.id,
      },
      row: { id: r.id, original: r.original, index: r.index },
    })),
  });
  const shimCell = (c) => ({
    value: c.getValue(),
    column: {
      ...(c.column.columnDef.meta?.v7 ?? {}),
      id: c.column.id,
    },
    row: {
      id: c.row.id,
      original: c.row.original,
      index: c.row.index,
    },
  });
  const shimColumn = (col) => ({
    ...(col.columnDef.meta?.v7 ?? {}),
    id: col.id,
    isSorted: !!col.getIsSorted(),
    isSortedDesc: col.getIsSorted() === 'desc',
  });

  const renderNoSearchMessage = () => {
    return displayRows?.length <= 0 ? (
      <Box
        display="flex"
        sx={{ height: 'calc(100vh  - 310px)' }}
        flexGrow={1}
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
      >
        <NoDataPresentComponent message={noSearchResultMessage} />
      </Box>
    ) : null;
  };

  const renderContextMenu = () => {
    return rowLevelRightClickMenus?.length ? (
      <ContextMenus
        menus={rowLevelRightClickMenus}
        additionalData={{ test: '1' }}
        menuId={menuId}
      />
    ) : null;
  };

  const renderTablePagination = () =>
    enablePagination && (
      <TablePagination
        rowsPerPageOptions={[
          ...(rowsPerPageOptions
            ? rowsPerPageOptions.map((ele) => ({
                label: `${ele} rows`,
                value: ele,
              }))
            : [5, 10, 20, 50].map((ele) => ({
                label: `${ele} rows`,
                value: ele,
              }))),
          ...(isAllOptionDisabled
            ? []
            : [
                {
                  label: 'All',
                  value: enableControlledPagination
                    ? allOptionValueSetter(rowsPerPageOptions, dataLength)
                    : allOptionValueSetter(
                        rowsPerPageOptions,
                        prePaginatedRows?.length
                      ),
                },
              ]),
        ]}
        colSpan={columns?.length}
        count={
          enableControlledPagination ? dataLength : prePaginatedRows?.length
        }
        rowsPerPage={pagination.pageSize}
        page={pagination.pageIndex}
        slots={{
          select: {
            props: {
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            },
          },
        }}
        onPageChange={handleOnPageChange}
        onRowsPerPageChange={handleOnRowPerPageChange}
        labelRowsPerPage="Show:"
        {...(labelDisplayedRows ? { labelDisplayedRows } : {})}
        ActionsComponent={
          showPaginationAction ? ReactMuiTablePaginationActions : undefined
        }
      />
    );

  const getFixedSizeListHeight = () => {
    if (enablePagination) {
      return pagination.pageSize < (data?.length ?? 0)
        ? pagination.pageSize * itemHeightForVirtualization
        : maxHeightForVirtualization;
    }
    return maxHeightForVirtualization;
  };

  const renderBodyCells = (row) =>
    row.getVisibleCells().map((cell) => {
      const cellShim = shimCell(cell);
      const showTitle =
        cell.column.columnDef.meta?.v7?.Cell?.name === 'defaultRenderer2';
      const columnPropsResult = getColumnProps(cellShim.column) || {};
      const cellPropsResult = getCellProps(cellShim) || {};
      const cSize = cell.column.getSize();
      return (
        <TableCell
          className="list-table-cell"
          key={cell.id}
          onClick={(e) => e.stopPropagation()}
          {...columnPropsResult}
          {...cellPropsResult}
          style={{
            boxSizing: 'border-box',
            flex: `${cSize} 0 auto`,
            minWidth: 0,
            width: cSize,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            wordBreak: 'initial',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            height: '50px',
            ...(columnPropsResult.style || {}),
            ...(cellPropsResult.style || {}),
          }}
          {...(showTitle ? { title: cellShim.value } : {})}
        >
          <span
            className="cellCommonProperty"
            style={{ display: 'block', width: '100%' }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </span>
        </TableCell>
      );
    });

  const parentRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (parentRef.current) {
      const updateWidth = () => {
        setContainerWidth(parentRef.current?.offsetWidth - 10 || 0);
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(parentRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, []);

  const tableWidth = useMemo(
    () => Math.max(table.getTotalSize() || 0, containerWidth || 0),
    [table, containerWidth, table.getState().columnSizing]
  );

  // Only wire the virtualizer to the scroll element when virtualization is
  // enabled. Returning null otherwise keeps the hook call-site stable (hooks
  // can't be conditional) while preventing it from attaching scroll listeners
  // that would fire on every scroll and re-render every cell.
  const virtualizer = useVirtualizer({
    count: enableVirtualization ? displayRows.length : 0,
    getScrollElement: () => (enableVirtualization ? parentRef.current : null),
    estimateSize: () => itemHeightForVirtualization,
    overscan: 8,
    enabled: enableVirtualization,
  });
  const virtualItems = enableVirtualization
    ? virtualizer.getVirtualItems()
    : [];
  const virtualTotalSize = enableVirtualization
    ? virtualizer.getTotalSize()
    : 0;

  return (
    <Box width="100%">
      <ThemeProvider theme={newTheme}>
        <TableContainer
          ref={parentRef}
          onScroll={rowLevelRightClickMenus?.length ? hideAll : undefined}
          style={{
            ...(stickyHeader
              ? {
                  maxHeight: maxTableHeight,
                  ...stickyHeaderConfig.containerStyle,
                }
              : {}),
            ...(enableVirtualization
              ? {
                  maxHeight: getFixedSizeListHeight(),
                  overflowY: 'auto',
                }
              : {}),
            ...(!data?.length ? { display: 'flex', alignItems: 'center' } : {}),
          }}
        >
          {data?.length && columns?.length ? (
            <Table
              size="medium"
              style={{
                width: `${tableWidth}px`,
              }}
              sx={{
                display: 'block',
                borderCollapse: 'collapse',
                '& .MuiTableHead-root, & .MuiTableBody-root': {
                  display: 'block',
                },
              }}
            >
              <TableHead
                style={
                  stickyHeader
                    ? {
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor:
                          newTheme?.components?.MuiTableCell?.styleOverrides
                            ?.head?.backgroundColor ||
                          newTheme.palette.background.paper,
                        ...stickyHeaderConfig.tableHeadStyle,
                      }
                    : {}
                }
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    scope="row"
                    style={{
                      display: 'flex',
                      minWidth: table.getTotalSize(),
                    }}
                  >
                    {headerGroup.headers.map((header) => {
                      const v7Col = header.column.columnDef.meta?.v7 ?? {};
                      const colShim = shimColumn(header.column);
                      const columnPropsResult = getColumnProps(colShim) || {};
                      const headerPropsResult = getHeaderProps(colShim) || {};
                      const canSort =
                        header.column.getCanSort() && !v7Col.disableSortBy;
                      const sortDir = header.column.getIsSorted();
                      const skipSortIds = [
                        'selection',
                        'expander',
                        'pk',
                        'view',
                        'bot',
                        '',
                      ];
                      const hSize = header.getSize();
                      return (
                        <TableCell
                          key={header.id}
                          align="center"
                          variant="head"
                          className="list-table-header"
                          scope="col"
                          aria-sort={
                            sortDir
                              ? sortDir === 'desc'
                                ? 'descending'
                                : 'ascending'
                              : 'none'
                          }
                          sx={{ textTransform: 'inherit' }}
                          {...columnPropsResult}
                          {...headerPropsResult}
                          style={{
                            boxSizing: 'border-box',
                            flex: `${hSize} 0 auto`,
                            minWidth: 0,
                            width: hSize,
                            position: 'relative',
                            ...(columnPropsResult.style || {}),
                            ...(headerPropsResult.style || {}),
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}

                          {!skipSortIds.includes(header.column.id) ? (
                            <Box
                              onClick={
                                canSort && header.column.id !== 'selection'
                                  ? header.column.getToggleSortingHandler()
                                  : undefined
                              }
                              style={
                                canSort ? { cursor: 'pointer' } : undefined
                              }
                            >
                              {sortDir ? (
                                <IconButton
                                  aria-label={`Sort by ${v7Col?.heading}`}
                                  tabIndex={0}
                                  sx={{ width: '12px', height: '12px' }}
                                  aria-live="polite"
                                >
                                  <TableSortLabel
                                    aria-label={`Sorted ${
                                      sortDir === 'desc'
                                        ? 'descending'
                                        : 'ascending'
                                    }`}
                                    component="span"
                                    active={!!sortDir}
                                    direction={
                                      sortDir === 'desc' ? 'desc' : 'asc'
                                    }
                                  />
                                </IconButton>
                              ) : (
                                sortedButton(v7Col)
                              )}
                            </Box>
                          ) : null}
                          <Divider
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            orientation="vertical"
                            className={`resizer ${
                              header.column.getIsResizing() ? 'isResizing' : ''
                            }`}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>

              {enableVirtualization ? (
                <TableBody
                  style={{
                    display: 'block',
                    position: 'relative',
                    height: virtualTotalSize,
                    minWidth: table.getTotalSize(),
                  }}
                >
                  {virtualItems.map((vi) => {
                    const row = displayRows[vi.index];
                    if (!row) return null;
                    const rowShim = shimRow(row);
                    const rowPropsResult =
                      getRowProps(rowShim, newTheme, (v) =>
                        table.toggleAllRowsSelected(v ?? false)
                      ) || {};
                    return (
                      <TableRow
                        key={row.id}
                        data-index={vi.index}
                        tabIndex={0}
                        aria-label={`${row.id}`}
                        {...rowPropsResult}
                        style={{
                          display: 'flex',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          minWidth: table.getTotalSize(),
                          height: vi.size,
                          transform: `translateY(${vi.start}px)`,
                          ...(rowPropsResult.style || {}),
                        }}
                      >
                        {renderBodyCells(row)}
                      </TableRow>
                    );
                  })}
                </TableBody>
              ) : (
                <TableBody>
                  {displayRows?.map((row, i) => {
                    const rowShim = shimRow(row);
                    const rowPropsResult =
                      getRowProps(rowShim, newTheme, (v) =>
                        table.toggleAllRowsSelected(v ?? false)
                      ) || {};
                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          tabIndex={0}
                          aria-label={`${row.id}`}
                          {...rowPropsResult}
                          style={{
                            display: 'flex',
                            minWidth: table.getTotalSize(),
                            ...(rowPropsResult.style || {}),
                          }}
                        >
                          {!enableRowSelection
                            ? rowLevelOnHoverOptions({
                                className: `row-level-options-on-hover-${i}`,
                                containerSx:
                                  ROW_LEVEL_HOVER_OPTIONS_DEFAULT_STYLE,
                                rowIndex: i,
                                row: rowShim,
                              })
                            : ''}
                          {row.getVisibleCells().map((cell) => {
                            const cellShim = shimCell(cell);
                            const showTitle =
                              cell.column.columnDef.meta?.v7?.Cell?.name ===
                              'defaultRenderer2';
                            const columnPropsResult =
                              getColumnProps(cellShim.column) || {};
                            const cellPropsResult =
                              getCellProps(cellShim) || {};
                            const cSize = cell.column.getSize();
                            return (
                              <TableCell
                                className="list-table-cell"
                                key={cell.id}
                                onClick={(e) => e.stopPropagation()}
                                {...columnPropsResult}
                                {...cellPropsResult}
                                style={{
                                  boxSizing: 'border-box',
                                  flex: `${cSize} 0 auto`,
                                  minWidth: 0,
                                  width: cSize,
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  wordBreak: 'initial',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  height: '50px',
                                  ...(columnPropsResult.style || {}),
                                  ...(cellPropsResult.style || {}),
                                }}
                                {...(showTitle
                                  ? { title: cellShim.value }
                                  : {})}
                              >
                                <span
                                  className="cellCommonProperty"
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                  }}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </span>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {row.getIsExpanded() && renderRowSubComponent ? (
                          <TableRow
                            className="renderRowSubComponent"
                            style={{
                              display: 'flex',
                              width: '100%',
                              minWidth: table.getTotalSize(),
                            }}
                          >
                            <TableCell
                              colSpan={row.getVisibleCells().length}
                              sx={{ border: 'none' }}
                              style={{
                                display: 'block',
                                flex: '1 1 auto',
                                width: '100%',
                                padding: 0,
                              }}
                            >
                              {renderRowSubComponent({ row: rowShim })}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          ) : null}
          {renderNoSearchMessage()}
        </TableContainer>
        <Box width="100%" overflow="auto">
          {renderTablePagination()}
        </Box>
        {renderContextMenu()}
      </ThemeProvider>
    </Box>
  );
};

ReactMuiTableListViewV8.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  getHeaderProps: PropTypes.func,
  getColumnProps: PropTypes.func,
  getRowProps: PropTypes.func,
  getCellProps: PropTypes.func,
  rowLevelOnHoverOptions: PropTypes.func,
  onTableStateChange: PropTypes.func,
  enableRowSelection: PropTypes.bool,
  toggle: PropTypes.bool,
  selectedRowIds: PropTypes.shape({}),
  externalCustomTheme: PropTypes.oneOfType([PropTypes.object]),
  rowSelectionConfig: PropTypes.oneOfType([PropTypes.object]),
  rowLevelRightClickMenus: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.object])
  ),
  fetchData: PropTypes.func,
  enableControlledPagination: PropTypes.bool,
  dataLength: PropTypes.number,
  labelDisplayedRows: PropTypes.func,
  showPaginationAction: PropTypes.bool,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  disableRowSelectionCondition: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  maxTableHeight: PropTypes.number,
};

export default ReactMuiTableListViewV8;
